import { useTranslation } from "react-i18next";
import { useOrientation, useWindowSize } from "react-use";
import { notifications } from "@mantine/notifications";
import { useMutation } from "@tanstack/react-query";

import { api } from "@utils/api";
import {
  showErrorNotification,
  showSuccessNotification,
} from "@utils/showNotification";

import { AUTO_CLOSE } from "@constants/index";

import { useTelemetryStore } from "@hooks/store/useTelemetryStore";


interface MutationProps {
  username: string;
  password: string;
}

export const useSignIn = () => {
  const store = useTelemetryStore();
  const { t, i18n } = useTranslation();
  const { width, height } = useWindowSize();
  const { type: orientation } = useOrientation();

  return useMutation({
    mutationFn: (formData: MutationProps) => {
      return api.post("token", formData, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });
    },
    onSuccess: (res: any) => {
      // console.log(res.data._id);
      if (!store.userId || store.userId !== res.data._id) {
        if (!store.userId) {
          import.meta.env.DEV &&
            console.log(
              "[Telemetry] - No user found, resetting with new user",
              new Date().toISOString()
            );
        } else {
          import.meta.env.DEV &&
            console.log(
              "[Telemetry] - Different user found, resetting with new user",
              new Date().toISOString()
            );
        }
        store.resetTelemetry();
        store.setTimestamp(new Date().toISOString());
      }

      store.setUserId(res.data._id);
      store.setSignInAttempts(prev => [
        ...prev,
        {
          timestamp: new Date().toISOString(),
          status: "success",
        },
      ]);
      showSuccessNotification({
        title: t("welcome"),
        message: t("sign-in-success"),
      });
      store.setScreenOrientation(orientation);
      store.setScreenResolution(`${width}x${height}`);
      store.setLanguage(i18n.language);
      setTimeout(() => {
        notifications.clean();
      }, AUTO_CLOSE);
    },

    onError: (res: any) => {
      console.error("[From Login]", res);
      store.setSignInAttempts(prev => [
        ...prev,
        {
          timestamp: new Date().toISOString(),
          status: "failed",
        },
      ]);

      switch (res?.response?.status) {
        case 401:
        case 404:
          showErrorNotification({
            title: "invalid-credentials-title",
            message: "invalid-credentials-message",
          });
          break;
        case 400:
        case 422:
          showErrorNotification({
            title: "invalid-input-title",
            message: "invalid-input-message",
          });
          break;
        default:
          showErrorNotification({ title: "oops", message: "unexpected-error" });
          break;
      }
    },
  });
};
